import { toNullableApiDate } from "@/lib/businessDate";
import type { SowingInstructionItem } from "./sowingInstructionsApi";

export function normalizeSowingInstructionItem(item: SowingInstructionItem): SowingInstructionItem {
  const scheduledSowingDate = toNullableApiDate(item.scheduledSowingDate);
  if (!scheduledSowingDate) throw new RangeError("Scheduled sowing date is required.");
  if (!Number.isFinite(item.quantityToSow) || item.quantityToSow <= 0) {
    throw new RangeError("Quantity to sow must be greater than zero.");
  }
  return {
    ...item,
    scheduledSowingDate,
    expectedGerminationDate: toNullableApiDate(item.expectedGerminationDate),
    assignedToUserId: item.assignedToUserId || null,
  };
}

export function sowingQuantityLabel(unit: number, unitName?: string) {
  return unit === 11 ? "Quantity (Trays)" : unit === 5 ? "Quantity (Seedlings/Pieces)"
    : unitName ? `Quantity (${unitName})` : "Quantity to Sow";
}

/** Server indices refer to the confirmed request, not the currently displayed selection. */
export function mapSowingFieldErrors(
  errors: Record<string, string[]> | undefined,
  submittedIds: string[],
): Record<string, Record<string, string[]>> {
  const rows: Record<string, Record<string, string[]>> = {};
  for (const [path, messages] of Object.entries(errors ?? {})) {
    const match = /^(?:\$\.)?items\[(\d+)\]\.(.+)$/i.exec(path);
    if (!match) continue;
    const id = submittedIds[Number(match[1])];
    if (!id) continue;
    const field = match[2][0].toLowerCase() + match[2].slice(1);
    (rows[id] ??= {})[field] = messages;
  }
  return rows;
}

export interface SowingInstructionForm {
    orderItemId: string | null;
    seedBatchId?: string;
    purpose: number;
    seedSource: number;
    cropTypeId: string;
    cropVarietyId?: string;
    variety: string;
    varietyPricingId?: string;
    quantityToSow: string;
    unit: number;
    trayType?: number;
    germinationChamberId: string;
    sowingLocation: string;
    scheduledSowingDate: string;
    expectedGerminationDate: string;
    expectedGerminationDateManuallySet?: boolean;
    assignedToUserId: string;
    specialInstructions: string;
    notes: string;
    plannedPlantingDate?: string;
    productionStageRecommendation?: string;
    requiredInputs: Array<{
        inputId: string;
        quantityNeeded: string;
        unit: number;
        notes: string;
    }>;
}

export function buildSowingInstructionItem(form: SowingInstructionForm): SowingInstructionItem {
  return normalizeSowingInstructionItem({
    orderItemId: form.orderItemId || undefined,
    seedBatchId: form.seedBatchId || undefined,
    purpose: form.orderItemId ? 1 : form.purpose,
    seedSource: form.seedSource,
    cropTypeId: form.cropTypeId,
    variety: form.variety,
    varietyPricingId: form.varietyPricingId || undefined,
    quantityToSow: Number(form.quantityToSow),
    unit: form.unit,
    trayType: form.trayType,
    germinationChamberId: form.germinationChamberId || undefined,
    sowingLocation: form.sowingLocation || undefined,
    scheduledSowingDate: form.scheduledSowingDate,
    expectedGerminationDate: form.expectedGerminationDate,
    assignedToUserId: form.assignedToUserId,
    specialInstructions: form.specialInstructions || undefined,
    notes: form.notes || undefined,
    requiredInputs: form.requiredInputs.map((input) => ({
      inputId: input.inputId,
      quantityNeeded: Number(input.quantityNeeded),
      unit: input.unit,
      notes: input.notes || undefined,
    })),
  });
}

/** Use configured lifecycle days only; unknown durations require a user's date. */
export function suggestGerminationDate(
  scheduledSowingDate: string,
  varietyDays?: number | null,
  cropDays?: number | null,
): string | null {
  const days = varietyDays ?? cropDays;
  if (days == null || !Number.isSafeInteger(days) || days <= 0 || !scheduledSowingDate) return null;
  try {
    const date = new Date(`${toNullableApiDate(scheduledSowingDate)}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return toNullableApiDate(date.toISOString());
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    return null; // Invalid sowing input is reported by validateSowingDates.
  }
}

/** Preserve explicit user edits when sowing dates or lookup data change. */
export function applyGerminationSuggestion(form: SowingInstructionForm, suggestion: string | null): SowingInstructionForm {
  const date = suggestion ?? "";
  return form.expectedGerminationDateManuallySet || form.expectedGerminationDate === date
    ? form : { ...form, expectedGerminationDate: date };
}

/** The creation form requires a known date; the API remains nullable for compatibility. */
export function validateSowingDates(form: Pick<SowingInstructionForm, "scheduledSowingDate" | "expectedGerminationDate">): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  const dates: Partial<Record<"scheduledSowingDate" | "expectedGerminationDate", string>> = {};
  for (const field of ["scheduledSowingDate", "expectedGerminationDate"] as const) {
    try {
      const date = toNullableApiDate(form[field]);
      if (!date) errors[field] = [field === "scheduledSowingDate"
        ? "Scheduled sowing date is required." : "Enter an expected germination date before creating this instruction."];
      else dates[field] = date;
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
      errors[field] = [field === "expectedGerminationDate" ? "Expected germination date is invalid." : "Scheduled sowing date is invalid."];
    }
  }
  if (dates.expectedGerminationDate && dates.scheduledSowingDate && dates.expectedGerminationDate <= dates.scheduledSowingDate) {
    errors.expectedGerminationDate = ["Expected germination date must be after scheduled sowing date."];
  }
  return errors;
}
